import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';

interface FileSelectionModalProps {
  CancelButtonText?: string;
  ConfirmSelection: (fileName: string) => void;
  HideModal: () => void;
  ImportButtonText?: string;
  IsLoading?: boolean;
  Message?: string;
  ShowModal: boolean;
  Title?: string;
  AvailableFiles?: string[];
  FilesLoading?: boolean;
  ImportJobStatus?: {
    status: string;
    processedCount: number;
    totalCount: number;
    matchedRows: number;
    unmatchedRows: number;
    progressPercentage: number;
    errorMessage?: string;
  } | null;
}

const FileSelectionModal = ({
  CancelButtonText = 'Peruuta',
  ConfirmSelection,
  HideModal,
  ImportButtonText = 'Tuo',
  IsLoading = false,
  Message = 'Valitse avoimien saldojen CSV-tiedosto tuontia varten.',
  ShowModal,
  Title = 'Valitse tiedosto tuotavaksi',
  AvailableFiles = [],
  FilesLoading = false,
  ImportJobStatus = null,
}: FileSelectionModalProps) => {
  const [selectedFile, setSelectedFile] = useState('');

  useEffect(() => {
    if (AvailableFiles && AvailableFiles.length > 0 && !selectedFile) {
      setSelectedFile(AvailableFiles[0]);
    }
  }, [AvailableFiles, selectedFile]);

  const isImporting = ImportJobStatus?.status === 'Processing';
  const isCompleted = ImportJobStatus?.status === 'Completed';
  const isFailed = ImportJobStatus?.status === 'Failed';
  const showFileSelection = !isImporting && !isCompleted && !isFailed;

  const handleConfirmSelection = () => {
    if (selectedFile) {
      ConfirmSelection(selectedFile);
    }
  };

  return (
    <Modal show={ShowModal} centered backdrop={isImporting ? 'static' : true}>
      <Modal.Body className="px-24 py-40 px-lg-40">
        <h3 className="h4 mb-16">
          {isImporting || isCompleted || isFailed
            ? 'Tuonnin edistyminen'
            : Title}
        </h3>

        {isImporting && ImportJobStatus && (
          <div>
            <p className="mb-16">
              {`Käsitellään rivejä: ${ImportJobStatus.processedCount} / ${ImportJobStatus.totalCount}`}
            </p>
            <div className="progress mb-3">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${ImportJobStatus.progressPercentage}%` }}
                aria-valuenow={ImportJobStatus.progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {`${ImportJobStatus.progressPercentage}%`}
              </div>
            </div>
            <p className="mb-0 text-muted small">
              {`Yhdistetyt: ${ImportJobStatus.matchedRows} | Yhdistämättömät: ${ImportJobStatus.unmatchedRows}`}
            </p>
          </div>
        )}

        {isCompleted && ImportJobStatus && (
          <div className="alert alert-success">
            <p className="mb-2">
              <strong>Tuonti valmis!</strong>
            </p>
            <p className="mb-0">
              {`Yhdistetyt rivit: ${ImportJobStatus.matchedRows}`}
              <br />
              {`Yhdistämättömät rivit: ${ImportJobStatus.unmatchedRows}`}
            </p>
          </div>
        )}

        {isFailed && ImportJobStatus && (
          <div className="alert alert-danger">
            <p className="mb-0">
              <strong>Virhe:</strong>
              {` ${ImportJobStatus.errorMessage}`}
            </p>
          </div>
        )}

        {showFileSelection && (
          <>
            <p className="mb-16">{Message}</p>

            {FilesLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Ladataan...</span>
                </Spinner>
              </div>
            )}

            {!FilesLoading && AvailableFiles && AvailableFiles.length > 0 && (
              <Form.Group controlId="fileSelect">
                <Form.Label>Tiedostot (uusin ensin)</Form.Label>
                <Form.Select
                  value={selectedFile}
                  onChange={e => setSelectedFile(e.target.value)}
                  size="sm"
                >
                  {AvailableFiles.map(file => (
                    <option key={file} value={file}>
                      {file}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {!FilesLoading && !AvailableFiles && (
              <div className="alert alert-warning">
                Avoimien saldojen CSV-tiedostoja ei löytynyt FTP-kansiosta.
              </div>
            )}

            {!FilesLoading && AvailableFiles && AvailableFiles.length === 0 && (
              <div className="alert alert-warning">
                Avoimien saldojen CSV-tiedostoja ei löytynyt FTP-kansiosta.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 bg-light p-24">
        {showFileSelection && (
          <>
            <Button
              variant="link"
              onClick={() => HideModal()}
              className="px-32"
              disabled={IsLoading}
            >
              {CancelButtonText}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSelection}
              className="px-32"
              disabled={!selectedFile || IsLoading || FilesLoading}
            >
              {IsLoading && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
              )}
              {ImportButtonText}
            </Button>
          </>
        )}
        {isCompleted && (
          <Button
            variant="primary"
            onClick={() => HideModal()}
            className="px-32"
          >
            Sulje
          </Button>
        )}
        {isFailed && (
          <Button
            variant="danger"
            onClick={() => HideModal()}
            className="px-32"
          >
            Sulje
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FileSelectionModal;
export { FileSelectionModal };
export type { FileSelectionModalProps };
